import React from "react";
import { Loader2, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { inputClass, labelClass, btnPrimary, btnGhost } from "@/components/Brand";
import { PrintSizePicker } from "@/components/PrintSizePicker";
import { MandatoryPrintPicker } from "@/components/MandatoryPrintPicker";

const TemplatePicker = ({ templates, onApply }) => {
  const { t } = useI18n();

  return (
    <div data-testid="template-picker">
      <label className={labelClass}>{t("templates")}</label>
      {templates.length === 0 ? (
        <p className="mt-2 text-xs text-[#707070]">{t("no_templates_settings")}</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {templates.map((tpl) => (
            <button key={tpl.template_id} type="button" data-testid={`apply-template-${tpl.template_id}`}
              onClick={() => onApply(tpl)}
              className="border border-[#2A2A2A] px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase text-[#D4AF37] hover:border-[#D4AF37] transition-colors">
              {tpl.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PrintSettings = ({ form, setForm, printCatalog }) => {
  return (
    <>
      <PrintSizePicker catalog={printCatalog} selected={form.print_sizes}
        onChange={(sizes) => setForm((f) => ({ ...f, print_sizes: sizes }))} />

      <MandatoryPrintPicker catalog={printCatalog} items={form.print_quota_items}
        onChange={(items) => setForm((f) => ({ ...f, print_quota_items: items }))} />
    </>
  );
};

const PackagePresets = ({ presets, value, onPick }) => {
  const { t } = useI18n();

  return (
    <div>
      <label className={labelClass}>{t("package_price")}</label>
      <input data-testid="package-price-input" type="number" min="0" className={inputClass}
        value={value.package_price} onChange={(e) => onPick({ package_price: e.target.value })}
        placeholder="750000" />
      {presets.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="package-presets">
          {presets.map((p) => {
            const on = String(value.package_price) === String(p.price);
            return (
              <button key={p.price} type="button" data-testid={`package-preset-${p.price}`}
                onClick={() => onPick({ package_price: String(p.price), package_label: p.label })}
                title={p.label}
                className={`min-h-[40px] px-3 py-1.5 border text-[10px] tracking-[0.14em] uppercase transition-colors ${
                  on ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A8A8] hover:border-[#D4AF37]"
                }`}>
                {IDR(p.price)}
                <span className="block font-mono-accent text-[9px] text-[#707070] normal-case tracking-normal truncate max-w-[9rem]">
                  {p.label}
                </span>
              </button>
            );
          })}
          {value.package_price !== "" && (
            <button type="button" data-testid="package-preset-clear"
              onClick={() => onPick({ package_price: "", package_label: "" })}
              className="min-h-[40px] px-3 text-[10px] tracking-[0.16em] uppercase text-[#707070] hover:text-[#F3F3F1]">
              {t("clear")}
            </button>
          )}
        </div>
      )}
      <p className="mt-2 text-[11px] text-[#707070]">{t("package_presets_hint")}</p>
    </div>
  );
};

/** The create-gallery modal: templates, client details, quotas and print setup. */
export const NewProjectDialog = ({ form, setForm, templates, presets, busy, onSubmit, onClose, onApplyTemplate, onQuotaItem, printCatalog }) => {
  const { t } = useI18n();

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div data-testid="new-project-modal" className="w-full max-w-xl bg-[#0F0F0F] border border-[#2A2A2A] p-7 lg:p-10 fade-up">
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="eyebrow mb-2">{t("new_project")}</p>
            <h2 className="text-3xl tracking-tighter font-medium">{t("create_gallery")}</h2>
          </div>
          <button data-testid="close-modal-btn" onClick={onClose} className="text-[#707070] hover:text-[#F3F3F1]">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          <TemplatePicker templates={templates} onApply={onApplyTemplate} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>{t("project_code")}</label>
              <input data-testid="project-code-input" className={inputClass} value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="17899 Anita (WC)" />
            </div>
            <div>
              <label className={labelClass}>{t("project_title")}</label>
              <input data-testid="project-title-input" className={inputClass} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Anita & Dimas Wedding" required />
            </div>
            <div>
              <label className={labelClass}>{t("client_name")}</label>
              <input data-testid="client-name-input" className={inputClass} value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("client_email")}</label>
              <input data-testid="client-email-input" type="email" className={inputClass} value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("client_phone")}</label>
              <input data-testid="client-phone-input" className={inputClass} value={form.client_phone}
                onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="08123456789" />
            </div>
            <div>
              <label className={labelClass}>{t("extra_price_override")}</label>
              <input data-testid="extra-price-override-input" type="number" className={inputClass}
                value={form.extra_edit_price} onChange={(e) => setForm({ ...form, extra_edit_price: e.target.value })}
                placeholder="5000" />
            </div>
          </div>

          <PackagePresets presets={presets} value={form}
            onPick={(patch) => setForm((f) => ({ ...f, ...patch }))} />

          <div>
            <label className={labelClass}>{t("drive_link")}</label>
            <input data-testid="drive-link-input" className={inputClass} value={form.drive_link}
              onChange={(e) => setForm({ ...form, drive_link: e.target.value })}
              placeholder="https://drive.google.com/drive/folders/..." />
          </div>
          <label className="flex items-center gap-3 text-xs text-[#A8A8A8] cursor-pointer">
            <input data-testid="use-sample-photos-checkbox" type="checkbox" checked={form.use_sample_photos}
              onChange={(e) => setForm({ ...form, use_sample_photos: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" />
            {t("demo_photos")}
          </label>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>{t("edit_quota")}</label>
              <input data-testid="edit-quota-input" type="number" min="1" className={inputClass}
                value={form.edit_quota} onChange={(e) => setForm({ ...form, edit_quota: e.target.value })} required />
            </div>
            <div>
              <label className={labelClass}>{t("max_extra")}</label>
              <input data-testid="max-extra-input" type="number" min="0" className={inputClass}
                value={form.max_extra_edits} onChange={(e) => setForm({ ...form, max_extra_edits: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-3 text-xs text-[#A8A8A8] cursor-pointer">
            <input data-testid="allow-extra-checkbox" type="checkbox" checked={form.allow_extra_edits}
              onChange={(e) => setForm({ ...form, allow_extra_edits: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" />
            {t("allow_extra")}
          </label>

          <div data-testid="module-toggle">
            <label className={labelClass}>{t("project_module")}</label>
            <div className="mt-3 grid grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
              {[
                { key: "edit_print", label: t("module_edit_print") },
                { key: "edit_only", label: t("module_edit_only") },
              ].map((m) => (
                <button key={m.key} type="button" data-testid={`module-${m.key}-btn`}
                  onClick={() => setForm({ ...form, module: m.key })}
                  className={`py-3 text-[10px] tracking-[0.16em] uppercase transition-colors ${
                    form.module === m.key ? "bg-[#D4AF37] text-black" : "bg-[#0B0B0B] text-[#A8A8A8] hover:text-[#F3F3F1]"
                  }`}>
                  {m.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-[#707070]">{t("module_hint")}</p>
          </div>

          {form.module === "edit_print" && (
            <PrintSettings form={form} setForm={setForm} printCatalog={printCatalog} />
          )}

          <button type="button" data-testid="cancel-create-btn" onClick={onClose}
            className={`${btnGhost} w-full py-3.5`}>
            {t("cancel")}
          </button>

          <button data-testid="create-project-submit-btn" disabled={busy} className={`${btnPrimary} w-full py-4`}>
            {busy && <Loader2 size={14} className="animate-spin" />} {t("create_gallery")}
          </button>
        </form>
      </div>
    </div>
  );
};
